import { useEffect, useMemo, useState } from "react";
import { Instagram, Youtube, Globe } from "lucide-react";

const INSTAGRAM_ICON = <Instagram size={18} />;
const YOUTUBE_ICON = <Youtube size={18} />;
const DEFAULT_PLATFORM_ICON = <Globe size={18} />;

const getPlatformIcon = (name) => {
  if (name === "Instagram") return INSTAGRAM_ICON;
  if (name === "YouTube") return YOUTUBE_ICON;
  return DEFAULT_PLATFORM_ICON;
};

const COUNTRY_FLAGS = {
  Brazil: "🇧🇷",
  "United States": "🇺🇸",
  "Dominican Republic": "🇩🇴",
  China: "🇨🇳",
  India: "🇮🇳",
  "United Kingdom": "🇬🇧",
  Ireland: "🇮🇪",
  Australia: "🇦🇺",
  Other: "🌐",
};

const getFlag = (name) => COUNTRY_FLAGS[name] ?? "🌐";

const PLATFORM_STAT_CONFIG = {
  Instagram: [
    { label: "Followers", keys: ["followers"] },
    { label: "Following", keys: ["following"] },
    {
      label: "Engagement Rate",
      keys: ["engagementRate", "engagement"],
      formatter: "percent",
    },
  ],
  YouTube: [
    { label: "Subscribers", keys: ["subscribers"] },
    { label: "Videos", keys: ["videos", "videoCount"] },
    { label: "Total Views", keys: ["totalViews", "views"] },
  ],
};

const FALLBACK_PLATFORMS = ["Instagram", "YouTube"];

const DEFAULT_STAT_CONFIG = [
  { label: "Followers", keys: ["followers"] },
  { label: "Average Views", keys: ["views"] },
  { label: "Engagement", keys: ["engagement", "engagementRate"], formatter: "percent" },
];

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return parsed.toLocaleString();
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value).includes("%") ? String(value) : "—";
  return `${parsed.toFixed(2)}%`;
};

const getStatValue = (data, keys = []) => {
  if (!data || typeof data !== "object") return undefined;
  const key = keys.find((candidate) => data[candidate] !== undefined && data[candidate] !== null);
  return key ? data[key] : undefined;
};

const Stat = ({ label, value }) => (
  <div>
    <p className="text-2xl font-semibold text-gray-900">{value}</p>
    <p className="text-gray-500 text-sm mt-0.5">{label}</p>
  </div>
);

const LocationBar = ({ name, value }) => (
  <div className="mb-4">
    <div className="flex items-center justify-between text-sm mb-1.5">
      <span className="flex items-center gap-2">
        <span className="text-lg" aria-hidden>{getFlag(name)}</span>
        <span className="text-gray-700">{name}</span>
      </span>
      <span className="font-medium text-gray-900">{value}%</span>
    </div>
    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
      <div
        className="h-full bg-orange-400 rounded-full transition-all duration-300"
        style={{ width: `${Math.min(100, value)}%` }}
      />
    </div>
  </div>
);

const AgeBar = ({ label, value }) => (
  <div className="flex flex-col items-center flex-1 min-w-0">
    <span className="text-sm font-medium text-gray-900 mb-1">{value}%</span>
    <div className="w-full max-w-[48px] h-32 bg-gray-200 rounded-t overflow-hidden flex flex-col justify-end">
      <div
        className="w-full bg-orange-400 rounded-t transition-all duration-300"
        style={{ height: `${value}%` }}
      />
    </div>
    <p className="text-xs text-gray-600 mt-2 truncate w-full text-center" title={label}>
      {label}
    </p>
  </div>
);

const COLORS = [
  "#FF9933", // orange
  "#E5E7EB", // gray
  "#F59E0B", // yellow (non-binary / other)
  "#10B981", // green
];

const DonutChart = ({ data, size = 140, stroke = 18 }) => {
  if (!data || data.length === 0) return null;

  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  const total = data.reduce((sum, d) => sum + d.value, 0);

  let offset = 0;

  return (
    <div className="flex items-center gap-10 flex-wrap">
      {/* SVG Donut */}
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
          {data.map((item, index) => {
            const value = (item.value / total) * circumference;
            const dashArray = `${value} ${circumference - value}`;
            const dashOffset = -offset;

            offset += value;

            return (
              <circle
                key={item.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="transparent"
                stroke={COLORS[index % COLORS.length]}
                strokeWidth={stroke}
                strokeDasharray={dashArray}
                strokeDashoffset={dashOffset}
                strokeLinecap="round"
              />
            );
          })}
        </g>
      </svg>

      {/* Legend */}
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={item.label} className="flex items-center gap-3">
            <span
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: COLORS[index % COLORS.length] }}
            />
            <span className="text-sm font-medium text-gray-800">
              {item.label}
            </span>
            <span className="text-sm font-semibold text-gray-900">
              {item.value}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};


const Analytics = ({ analytics, isVerified = false }) => {

  
  const platforms = useMemo(() => {
    if (!analytics || typeof analytics !== "object") return [];
    return Object.entries(analytics).filter(
      ([_, data]) =>
        data &&
        typeof data === "object" &&
        Object.keys(data).length > 0
    );
  }, [analytics]);

  const connectedPlatformNames = platforms.map(([name]) => name);
  const [activePlatform, setActivePlatform] = useState(
    connectedPlatformNames[0] ?? FALLBACK_PLATFORMS[0]
  );

  useEffect(() => {
    if (connectedPlatformNames.length === 0) {
      setActivePlatform(FALLBACK_PLATFORMS[0]);
      return;
    }

    if (!connectedPlatformNames.includes(activePlatform)) {
      setActivePlatform(connectedPlatformNames[0]);
    }
  }, [activePlatform, connectedPlatformNames]);

  const activeData = useMemo(
    () => (activePlatform && analytics?.[activePlatform]) || null,
    [activePlatform, analytics]
  );

  const statConfig = PLATFORM_STAT_CONFIG[activePlatform] || DEFAULT_STAT_CONFIG;

  if (!isVerified) {
    return (
      <div>
        <h2 className="text-2xl font-semibold mb-8">Analytics</h2>
        <div className="rounded-xl border border-gray-200 bg-white p-6 sm:p-8">
          <div className="bg-gray-100 text-gray-500 text-sm rounded-lg py-4 px-6 text-center">
            Analytics are available only for verified influencers.
          </div>
        </div>
      </div>
    );
  }

  if (!analytics || Object.keys(analytics).length === 0) {
    return (
      <div>
        <h2 className="text-2xl font-semibold mb-8">Analytics</h2>
  
        <div className="rounded-xl border border-gray-200 bg-white p-6 sm:p-8">
          <div className="flex gap-8 border-b border-gray-200 mb-8">
            {FALLBACK_PLATFORMS.map((platformName) => {
              const isActive = activePlatform === platformName;
              return (
                <button
                  key={platformName}
                  onClick={() => setActivePlatform(platformName)}
                  className={`flex items-center gap-2 pb-4 -mb-px font-medium text-sm transition-colors ${
                    isActive
                      ? "text-blue-600 border-b-2 border-blue-600"
                      : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
                  }`}
                >
                  {getPlatformIcon(platformName)}
                  {platformName}
                </button>
              );
            })}
          </div>
  
          <div className="bg-gray-100 text-gray-500 text-sm rounded-lg py-4 px-6 text-center">
            Connect Instagram or YouTube to display analytics on this profile.
          </div>
        </div>
      </div>
    );
  }

  if (connectedPlatformNames.length === 0) {
    return (
      <div>
        <h2 className="text-2xl font-semibold mb-8">Analytics</h2>
        <div className="rounded-xl border border-gray-200 bg-white p-6 sm:p-8">
          <div className="bg-gray-100 text-gray-500 text-sm rounded-lg py-4 px-6 text-center">
            Connect Instagram or YouTube to display analytics on this profile.
          </div>
        </div>
      </div>
    );
  }
  

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-8">Analytics</h2>

      <div className="rounded-xl border border-gray-200 bg-white p-6 sm:p-8">
        {/* Platform tabs */}
        <div className="flex gap-8 border-b border-gray-200 mb-8">
          {connectedPlatformNames.map((platformName) => {
            const isActive = activePlatform === platformName;
            return (
              <button
                key={platformName}
                type="button"
                onClick={() => setActivePlatform(platformName)}
                className={`flex items-center gap-2 pb-4 -mb-px font-medium text-sm transition-colors ${isActive
                    ? "text-blue-600 border-b-2 border-blue-600"
                    : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
                  }`}
              >
                {getPlatformIcon(platformName)}
                {platformName}
              </button>
            );
          })}
        </div>

        {!activeData && (
          <p className="text-gray-500 py-8">No analytics for this platform.</p>
        )}

        {activeData && (
          <>
            {/* Stats row */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 mb-10">
              {statConfig.map((item) => {
                const rawValue = getStatValue(activeData, item.keys);
                const value =
                  item.formatter === "percent"
                    ? formatPercent(rawValue)
                    : formatNumber(rawValue);
                return <Stat key={item.label} label={item.label} value={value} />;
              })}
            </div>

            {/* Location + Age */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-10">
              {(activeData.locations ?? []).length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Audience Location</h3>
                  {(activeData.locations ?? []).map((loc) => (
                    <LocationBar key={loc.name} name={loc.name} value={loc.value} />
                  ))}
                </div>
              )}

              {(activeData.ages ?? []).length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Audience Age</h3>
                  <div className="flex items-end gap-4 justify-between">
                    {(activeData.ages ?? []).map((a) => (
                      <AgeBar key={a.label} label={a.label} value={a.value} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Gender donut */}
            {(activeData.gender ?? []).length > 0 && (
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">
                  Audience Gender
                </h3>
                <DonutChart data={activeData.gender} size={120} />
              </div>
            )}

          </>
        )}
      </div>
    </div>
  );
};

export default Analytics;
